/**
 * Form locking decision logic.
 *
 * Centralises the business rules that determine whether a patient's
 * questionnaire forms should be locked (read-only) or editable.
 *
 * Forms are locked once the clinician begins the assessment (i.e. CI or SSI
 * AI report paragraphs have been generated), because the patient's answers
 * informed the clinician's decisions and changing them retroactively could
 * invalidate the clinical record.
 *
 * Two exemption mechanisms exist:
 *   1. Org-level — some organisations' workflows intentionally allow patients
 *      to continue editing forms throughout the assessment.
 *   2. Patient-level — a `formsUnlocked: true` flag on the patient document
 *      re-opens forms for an individual patient (e.g. when they did not finish
 *      their forms before the assessment started and the org has approved them
 *      completing them after the fact).
 *
 * CYP ADHD extends this with per-informant locking. The lock is computed
 * server-side and denormalised onto the informant's profile at
 * `activeAssignments.cyp_adhd.{patientId}.formsLocked`. The informant client
 * reads this flag via `isCypInformantAssignmentLocked`.
 */

import { timestampToMillis } from './timestampUtils';

const ORG_LEVEL_EXEMPTIONS = ['Psychological Consultations'];

/**
 * Some organisations never lock questionnaires, so an unlock action would
 * have nothing to do. Adult ADHD hides the control for these orgs.
 */
export const isFormLockOrgExempt = (organisation) =>
  ORG_LEVEL_EXEMPTIONS.includes(organisation);

/**
 * Whether a CYP appointment has generated its first report section.
 *
 * Adult ADHD reads ciReportGenerated / ssiReportGenerated on the patient
 * document and only then offers "Unlock questionnaire forms". CYP stores
 * the same flags on the clinical interview and semi-structured interview
 * report documents. The first generated section sets the flag. Until then
 * the forms are not locked, and unlocking early would keep them open
 * after the appointment starts. Only boolean true counts.
 *
 * @param {Object} reportContent - Map of stage key to report document
 * @returns {boolean}
 */
export const hasCypAppointmentReportBeenGenerated = (reportContent) => {
  const ciGenerated = reportContent?.clinicalInterview?.ciReportGenerated === true;
  const ssiGenerated = reportContent?.semiStructuredInterview?.ssiReportGenerated === true;
  return ciGenerated || ssiGenerated;
};

/** Shown on the disabled unlock row, and returned by the server if the UI is bypassed. */
export const FORM_LOCK_BEFORE_APPOINTMENT_MESSAGE =
  'Forms remain unlocked until an appointment and the first report section have been generated.';

/**
 * Determine whether a patient's forms should be locked.
 *
 * @param {Object} patientDataModel - The patient's full Firestore document
 * @returns {boolean} true if forms should be locked (read-only)
 */
export const computeIsFormLocked = (patientDataModel) => {
  const reportData = patientDataModel?.dataModel?.['6_manageReports'];
  const patientOrganisation = patientDataModel?.organisation;

  const reportsGenerated =
    reportData?.ciReportGenerated === true ||
    reportData?.ssiReportGenerated === true;

  const hasOrgLevelExemption = ORG_LEVEL_EXEMPTIONS.includes(patientOrganisation);
  const hasPatientLevelOverride = patientDataModel?.formsUnlocked === true;

  return reportsGenerated && !hasOrgLevelExemption && !hasPatientLevelOverride;
};

/**
 * Determine whether the "Regenerate AI outputs" button should be visible
 * for a given report section on the Documents Dashboard.
 *
 * The button is shown when:
 *   - The section is not locked
 *   - The current user is the assigned reviewer for that section
 *   - The patient's org has an ongoing-edits exemption OR the patient has
 *     the formsUnlocked override
 *
 * @param {Object} options
 * @param {string} options.sectionId - e.g. 'clinicalInterview' or 'semiStructuredInterview'
 * @param {string} options.sectionStatus - e.g. 'locked', 'approved', 'ready-for-review'
 * @param {string} options.reviewerClinicianId - UID of the assigned reviewer
 * @param {string} options.currentUserId - UID of the currently logged-in clinician
 * @param {string} options.organisation - The clinician's organisation claim
 * @param {boolean} options.formsUnlocked - Whether the patient has formsUnlocked === true
 * @returns {boolean}
 */
export const shouldShowRegenerateButton = ({
  sectionId,
  sectionStatus,
  reviewerClinicianId,
  currentUserId,
  organisation,
  formsUnlocked,
}) => {
  if (sectionStatus === 'locked') return false;
  if (reviewerClinicianId !== currentUserId) return false;

  if (sectionId === 'clinicalInterview') {
    return organisation === 'Psychological Consultations' || formsUnlocked === true;
  }

  if (sectionId === 'semiStructuredInterview') {
    return formsUnlocked === true;
  }

  return false;
};


// ── CYP ADHD per-informant locking ─────────────────────────────────────────

/**
 * Check whether a CYP ADHD informant's questionnaires are locked.
 *
 * Reads the denormalised `formsLocked` flag from the informant's own profile
 * (written by the server-side sync in `cyp_adhd_form_lock.py`). This is the
 * single source of truth for the informant client — the informant cannot read
 * the patient doc or its reportContent, so computing the lock client-side is
 * not possible.
 *
 * Identity-checked: only boolean `true` returns locked, matching the Python
 * `is True` contract. Missing keys and truthy non-booleans fail open.
 *
 * @param {Object} profile - The informant's Firestore profile (header) doc.
 * @param {string} patientId - The linked patient UID.
 * @returns {boolean} True if the informant's forms are locked.
 */
export const isCypInformantAssignmentLocked = (profile, patientId) => {
  if (!profile || !patientId) return false;
  const assignment =
    profile?.activeAssignments?.cyp_adhd?.[patientId];
  return assignment?.formsLocked === true;
};


/**
 * Find informant form instances updated after the CI/SSI report was generated.
 *
 * This is a stateless comparison: each form instance's `updatedAt` timestamp
 * is compared against `ciReportGeneratedAt` and `ssiReportGeneratedAt` on
 * the child's reportContent docs. Regenerating the report moves `GeneratedAt`
 * forward, so the warning self-clears without any flag writes.
 *
 * @param {Object} params
 * @param {Object} params.informantFormInstances - Map of informant UID to
 *   their form instances object (`{ [formKey]: { updatedAt, ... } }`).
 * @param {Object} params.reportContent - Map of stage key to reportContent
 *   doc data (e.g. `{ clinicalInterview: { ciReportGeneratedAt }, ... }`).
 * @returns {Object} Map of informant UID to change info, only for those
 *   with updates after generation. Each entry has `ci` and/or `ssi` keys
 *   with `{ latestUpdatedAt, formKeys }`. Empty object when nothing changed.
 */
export const findInformantAnswersChangedAfterGeneration = ({
  informantFormInstances = {},
  reportContent = {},
}) => {
  const ciDoc = reportContent?.clinicalInterview || {};
  const ssiDoc = reportContent?.semiStructuredInterview || {};

  const ciGenMs = timestampToMillis(ciDoc.ciReportGeneratedAt);
  const ssiGenMs = timestampToMillis(ssiDoc.ssiReportGeneratedAt);

  // If neither report has been generated there is nothing to compare.
  if (!ciGenMs && !ssiGenMs) return {};

  const result = {};

  for (const [informantId, formInstances] of Object.entries(informantFormInstances)) {
    if (!formInstances || typeof formInstances !== 'object') continue;

    const entry = {};

    for (const [formKey, fi] of Object.entries(formInstances)) {
      const updatedMs = timestampToMillis(fi?.updatedAt);
      if (!updatedMs) continue;

      // Check against CI generation timestamp
      if (ciGenMs && updatedMs > ciGenMs) {
        if (!entry.ci) entry.ci = { latestUpdatedAt: updatedMs, formKeys: [] };
        entry.ci.formKeys.push(formKey);
        if (updatedMs > entry.ci.latestUpdatedAt) {
          entry.ci.latestUpdatedAt = updatedMs;
        }
      }

      // Check against SSI generation timestamp
      if (ssiGenMs && updatedMs > ssiGenMs) {
        if (!entry.ssi) entry.ssi = { latestUpdatedAt: updatedMs, formKeys: [] };
        entry.ssi.formKeys.push(formKey);
        if (updatedMs > entry.ssi.latestUpdatedAt) {
          entry.ssi.latestUpdatedAt = updatedMs;
        }
      }
    }

    if (entry.ci || entry.ssi) {
      result[informantId] = entry;
    }
  }

  return result;
};
